import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { SelfUserStore, PermissionNumber } from '@person/user/self/data-access';
import { RouterStore } from '@route/data-access';

type RoutePermissions = number | string | Array<number | string>;

const TEMP_AUTH_PERMISSION_GUARD_PATH = 'TEMPORARY_AUTH_MODAL.PERMISSION_GUARD';

/**
 * Guard that ensures the user has the required permissions before activating a route.
 * Configure on the route using `data.requiredPermissions`, accepting:
 *  - a single permission (number or string enum key)
 *  - an array of permissions (numbers or string enum keys)
 *
 * Optionally, configure `data.translationKey` to use custom translation keys:
 *  data: { 
 *    requiredPermissions: [PermissionNumber.VisaoGeralAdministrador],
 *    translationKey: 'ADMINISTRATOR_GENERAL_VIEW'
 *  }
 *
 * Example:
 *  data: { requiredPermissions: ['ClienteObterPorId'] }
 *  or
 *  data: { requiredPermissions: [PermissionText.ClienteObterPorId] }
 */
export const PermissionGuard: CanActivateFn = async (route): Promise<boolean> => {
  const selfUserStore = inject(SelfUserStore);
  const routerStore = inject(RouterStore);
  const router = inject(Router);

  const required: RoutePermissions = route.data?.['requiredPermissions'];
  if (!required) return true;

  const list = Array.isArray(required) ? required : [required];
  const permissions: number[] = list
    .map((p) => {
      if (typeof p === 'number') return p;
      const byKey = PermissionNumber[p as keyof typeof PermissionNumber];
      if (typeof byKey === 'number') return byKey;
      const asNum = Number(p);
      return Number.isFinite(asNum) ? asNum : NaN;
    })
    .filter((v): v is number => typeof v === 'number' && !Number.isNaN(v));

  const isFirstNavigation = !router.navigated;
  if (Array.isArray(list) && list.length > 0 && permissions.length === 0) {
    console.warn('[PermissionGuard] No valid permissions resolved from route data.requiredPermissions:', list);
    if (isFirstNavigation) {
      const redirectTo = route.data?.['redirectTo'] ?? '/';
      routerStore.navigate(Array.isArray(redirectTo) ? redirectTo : [redirectTo]);
    }
    return false;
  }

  const interactive = !isFirstNavigation;
  const translationKey = route.data?.['translationKey'] as string | undefined;
  
  const config = translationKey ? {
    title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.${translationKey}.TITLE`,
    message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.${translationKey}.MESSAGE`,
  } : undefined;

  const ok = await selfUserStore.ensurePermissions(permissions, { interactive, ...config });

  if (!ok && isFirstNavigation) {
    const redirectTo = route.data?.['redirectTo'] ?? '/';
    routerStore.navigate(Array.isArray(redirectTo) ? redirectTo : [redirectTo]);
  }

  return ok;
};
